import { useGetAllAds } from "../../functions/adsHook";
import NewsletterSignup from "../../components/newsletter/newsletter";
import "../../index.css";
import { FC, useMemo } from "react";

interface Ad {
  title: string;
  description: string;
  type: "premium" | "regular";
  buttonText: string;
  buttonLink: string;
  id: string;
}

const AdvertisementSidebar: FC = () => {
  const { ads, isLoading, isError, error } = useGetAllAds(1, 10);

  const { premiumAds, regularAds } = useMemo(() => {
    const premiumAds = ads.filter((ad: Ad) => ad.type === "premium");
    const regularAds = ads.filter((ad: Ad) => ad.type !== "premium");
    return { premiumAds, regularAds };
  }, [ads]);

  return (
    <aside className="w-80 scrollbar-hidden bg-gray-50 border-l border-gray-100 flex-shrink-0 overflow-y-auto lg:block hidden">
      <div className="p-6">
        <div className="mb-8">
          <div className="mb-6 pb-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 tracking-wider uppercase">
              Sponsored Content
            </h3>
          </div>
          
          <div className="space-y-6">
            {isLoading &&
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl mb-3"></div>
                  <div className="h-4 bg-gray-100 rounded-full w-3/4 mb-3"></div>
                  <div className="h-3 bg-gray-100 rounded-full w-full mb-2"></div>
                  <div className="h-3 bg-gray-100 rounded-full w-2/3"></div>
                </div>
              ))}

            {!isLoading &&
              premiumAds.map((ad: Ad) => (
                <div
                  key={ad.id}
                  className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:border-blue-200 transition-all"
                >
                  <div className="mb-4">
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2.5">{ad.title}</h4>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {ad.description}
                  </p>
                  <a href={ad.buttonLink} target="_blank" rel="noopener noreferrer">
                    <button className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 px-4 py-2.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
                      {ad.buttonText || "Learn More"}
                    </button>
                  </a>
                </div>
              ))}

            {!isLoading &&
              regularAds.map((ad: Ad) => (
                <div
                  key={ad.title}
                  className="group bg-white rounded-xl p-5 hover:shadow-sm border border-gray-200 transition-all"
                >
                  <div className="mb-4 relative overflow-hidden rounded-lg aspect-video bg-gray-50 flex items-center justify-center">
                    <span className="text-xs text-gray-400 font-medium">
                      <svg
                        className="w-4 h-4 inline-block mr-2 -mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 9a2 2 0 10-4 0v5a2 2 0 01-2 2h6m-6-4h4m8 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Advertisement
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{ad.title}</h4>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {ad.description}
                  </p>
                  <a href={ad.buttonLink} target="_blank" rel="noopener noreferrer">
                    <button className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 transition-colors">
                      {ad.buttonText || "Learn More"}
                      <svg
                        className="w-4 h-4 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </a>
                </div>
              ))}

            <div className="pt-6 border-t border-gray-200">
              <NewsletterSignup />
            </div>
          </div>
        </div>
        
        {isError && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-100 flex items-center gap-3">
            <svg
              className="w-5 h-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-sm text-red-600">
              Error loading ads: {error?.message || "Unknown error"}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
};

export default AdvertisementSidebar;